import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';

/**
 * Reads the plain text of a `contenteditable` host. `innerText` is not
 * implemented in jsdom, hence the `textContent` fallback for tests.
 */
function readText(element: HTMLElement): string {
  return (element.innerText ?? element.textContent ?? '').replace(/\n$/, '');
}

/**
 * Drops the leftovers browsers keep in an edited host.
 *
 * Deleting the last characters leaves empty elements behind in Chromium, e.g.
 * the wrapper spans of removed emoji or a trailing `<br>`. They are invisible
 * but keep the host from matching `:empty`, which hides a CSS placeholder, and
 * they make the caret start inside stale markup.
 */
function pruneEmptyNodes(element: HTMLElement): void {
  if (readText(element) === '') {
    if (element.firstChild) element.replaceChildren();
    return;
  }
  element
    // only spans: an empty `<br>` is a deliberate line break, not a leftover
    .querySelectorAll('span:empty')
    .forEach(node => node.remove());
}

export interface InsertOptions {
  /** Whether the host should be focused and the caret applied. */
  focus?: boolean;
}

export interface ContentEditableOptions {
  /**
   * Converts inserted plain text into DOM nodes, e.g. to wrap parts of it into
   * styled spans. Defaults to a single text node.
   */
  toNodes?: (text: string, emojiData: EmojiData) => Node[];
  /**
   * Called with the current text whenever the content changed, either by user
   * input or by {@link ContentEditableApi.insert}.
   */
  onChange?: (text: string, element: HTMLElement) => void;
  /**
   * Ref of the consumer that should also receive the editable host, e.g. the
   * `ref` prop of the surrounding component. Object and callback refs are both
   * supported.
   */
  ref?: React.Ref<HTMLElement>;
  emojiData?: EmojiData;
}

export interface ContentEditableApi {
  /** Current plain text of the host. */
  text: string;
  /**
   * Inserts `text` at the caret, converted through `toNodes`, and leaves the
   * caret right behind the insertion.
   */
  insert: (text: string, options?: InsertOptions) => void;
  /** Inserts ready-made nodes at the caret, e.g. a labelled element. */
  insertNodes: (nodes: Node[], options?: InsertOptions) => void;
  /** Removes all content. */
  clear: () => void;
  /** Focuses the host and restores the last caret position. */
  focus: () => void;
  element: React.RefObject<HTMLElement | null>;
  /** Props to spread onto the `contenteditable` element. */
  editableProps: Pick<
    React.HTMLAttributes<HTMLElement>,
    'onInput' | 'onKeyUp' | 'onMouseUp' | 'onBlur' | 'onPaste'
  > & {
    ref: (element: HTMLElement | null) => void;
    suppressContentEditableWarning: true;
  };
}

/**
 * Manages a `contenteditable` host.
 *
 * The host is intentionally uncontrolled: React must not render its children,
 * because replacing them would discard the browser selection, the undo stack
 * and any running IME composition. Content changes therefore go through the
 * DOM, and this hook keeps the resulting text mirrored in React state.
 */
export function useContentEditable({
  toNodes,
  onChange,
  emojiData = { categories: {}, emojis: {} },
  ref
}: Readonly<ContentEditableOptions> = {}): ContentEditableApi {
  const element = useRef<HTMLElement>(null);
  const [text, setText] = useState('');
  /*
   * Last caret position inside the host. Opening a picker or toolbar moves
   * focus away, and the browser would otherwise collapse the caret to the start
   * on the next focus.
   */
  const caret = useRef<Range | null>(null);

  const rememberCaret = useCallback(() => {
    const host = element.current;
    const selection = window.getSelection();
    if (!host || !selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (host.contains(range.commonAncestorContainer)) {
      caret.current = range.cloneRange();
    }
  }, []);

  const caretRangeIn = useCallback((host: HTMLElement): Range => {
    if (caret.current) return caret.current;
    const range = document.createRange();
    range.selectNodeContents(host);
    range.collapse(false);
    return range;
  }, []);

  const insertNodes = useCallback(
    (nodes: Node[], { focus = true }: InsertOptions = {}) => {
      const host = element.current;
      if (!host) return;
      if (focus) host.focus();

      const range = caretRangeIn(host);
      const fragment = document.createDocumentFragment();
      fragment.append(...nodes);
      const lastNode = fragment.lastChild;

      range.deleteContents();
      range.insertNode(fragment);
      if (lastNode) {
        /*
         * Chromium collapses a caret placed behind an element back into that
         * element, so text typed after an emoji would end up inside its span.
         * An adjacent text node gives the caret a place of its own outside the
         * span; an empty one is enough, it either receives the typed text or is
         * dropped with the rest once the host is emptied.
         */
        if (lastNode.nodeType === Node.TEXT_NODE) {
          range.setStartAfter(lastNode);
        } else {
          const sibling = lastNode.nextSibling;
          const parent = lastNode.parentNode ?? host;
          const landing =
            sibling?.nodeType === Node.TEXT_NODE
              ? sibling
              : parent.insertBefore(document.createTextNode(''), sibling);
          range.setStart(landing, 0);
        }
      }
      range.collapse(true);
      caret.current = range.cloneRange();

      if (focus) {
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }

      const current = readText(host);
      setText(current);
      onChange?.(current, host);
    },
    [caretRangeIn, onChange]
  );

  const insert = useCallback(
    (value: string, options?: InsertOptions) =>
      insertNodes(
        toNodes?.(value, emojiData) ?? [document.createTextNode(value)],
        options
      ),
    [emojiData, insertNodes, toNodes]
  );

  const clear = useCallback(() => {
    element.current?.replaceChildren();
    caret.current = null;
    setText('');
  }, []);

  const focus = useCallback(() => {
    const host = element.current;
    if (!host) return;
    host.focus();
    // put the caret back where it was, focus() alone collapses it to the start
    const range = caret.current;
    if (range && host.contains(range.commonAncestorContainer)) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, []);

  const handleInput = useCallback(
    (event: React.FormEvent<HTMLElement>) => {
      const host = event.currentTarget;
      const current = readText(host);
      pruneEmptyNodes(host);
      // an emptied host has no node left the remembered caret could point into
      if (current === '') caret.current = null;
      else rememberCaret();
      setText(current);
      onChange?.(current, host);
    },
    [onChange, rememberCaret]
  );

  /* Paste as plain text, so no foreign markup enters the host. */
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLElement>) => {
      event.preventDefault();
      rememberCaret();
      insert(event.clipboardData.getData('text/plain'));
    },
    [insert, rememberCaret]
  );

  const setRef = useCallback(
    (host: HTMLElement | null) => {
      element.current = host;
      if (typeof ref === 'function') ref(host);
      else if (ref) ref.current = host;
    },
    [ref]
  );

  return useMemo(
    () => ({
      text,
      insert,
      insertNodes,
      clear,
      focus,
      element,
      editableProps: {
        ref: setRef,
        suppressContentEditableWarning: true as const,
        onInput: handleInput,
        onPaste: handlePaste,
        onKeyUp: rememberCaret,
        onMouseUp: rememberCaret,
        onBlur: rememberCaret
      }
    }),
    [
      clear,
      focus,
      handleInput,
      handlePaste,
      insert,
      insertNodes,
      rememberCaret,
      setRef,
      text
    ]
  );
}
